// The rail's row counts — the numbers beside Repos, Pending and Blockers.
//
// Every count is a server read the app already makes, never a client tally:
//   repos    ← `GET /v1/overview/counts` `.repos_enabled`
//   pending  ← the same read's `.pending_approvals`
//   blockers ← `GET /v1/blockers?open=true`, the Blockers screen's own query key,
//              so the rail and the screen share one cache entry
//
// A count that is not honestly known is `undefined`, which the rail renders as
// no badge (`AppNav`): still loading, refused (a token without the scope), or —
// for blockers — a FULL page. The blockers route mints no cursor and caps a read
// at `BLOCKER_PAGE_MAX`, so a full page says "at least that many", and a badge
// reading 200 would be a number the server never said.
//
// `useRailDots` is the STATUS twin (#4395, epic #4391): the WATCH group's
// Terminal row carries a DOT, not a count (D-O — "the rail shows a status dot,
// not a count"), painted at Fleet's own health verdict rather than a client
// re-derivation of it (D-C). Kept beside `useRailCounts` — same shape of
// consumer (`OrgRail.tsx`), same honesty rule, different badge.

import type { NavDotStatus } from '../components/AppNav';
import { useAgentActivity } from './agent-activity';
import { agentsRailDotStatus, useAgentsPreview } from './agents-preview';
import { BLOCKER_PAGE_MAX, useBlockers } from './blockers';
import { useOverviewCounts } from './overview-counts';
import { SESSION_TOKEN } from './session';
import { TERMINAL_RAIL_DOT_POLL_MS, useTerminalStatus } from './terminal-status';

export type RailCounts = {
  readonly [key: string]: (() => number | undefined) | undefined;
};

/**
 * The rows whose count is work somebody OWES: what a folded heading sums
 * (`lib/rail-rollup.ts`). `repos` is inventory, so it is not here.
 */
export const OWED_COUNT_KEYS: ReadonlySet<string> = new Set(['pending', 'blockers']);

export function useRailCounts(token: string | null = SESSION_TOKEN): RailCounts {
  const counts = useOverviewCounts(token);
  const blockers = useBlockers(() => true, token);
  return {
    repos: () => (counts.isError ? undefined : counts.data?.repos_enabled),
    pending: () => (counts.isError ? undefined : counts.data?.pending_approvals),
    blockers: () => {
      const rows = blockers.isError ? undefined : blockers.data;
      return rows === undefined || rows.length >= BLOCKER_PAGE_MAX ? undefined : rows.length;
    },
  };
}

export type RailDots = {
  readonly [key: string]: (() => NavDotStatus | undefined) | undefined;
};

export function useRailDots(token: string | null = SESSION_TOKEN): RailDots {
  const status = useTerminalStatus(token, TERMINAL_RAIL_DOT_POLL_MS);
  // Both reads are the Agents page's OWN — same query keys, so mounting the
  // page beside the rail (or two rail instances) shares one cache entry each
  // and arms no third poll (#5209, epic #5200).
  const activity = useAgentActivity(token);
  const preview = useAgentsPreview(token);
  return {
    terminal: () => (status.isError ? undefined : status.data?.status),
    agents: () =>
      agentsRailDotStatus(
        preview.isError ? undefined : preview.data?.purposes,
        activity.isError ? undefined : activity.data?.agents,
      ),
  };
}
